import { ArrowLeft, Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { adminService, walletService } from "@/services/apiServices";
import type { AdminWalletRow, WalletTxn } from "@/types/wallet";
import { DEFAULT_RANGE, formatDate, formatINR } from "@/pages/retailer/wallet/data";
import TransactionsTab from "./components/TransactionsTab";
import AddBalanceModal from "./components/AddBalanceModal";
import DeductBalanceModal from "./components/DeductBalanceModal";

function todayStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: todayStr(now), dateTo: todayStr(now) };
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "This Month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 3 Months": {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    default:
      return {};
  }
}

export default function AdminWalletLedger() {
  return (
    <ToastProvider>
      <LedgerInner />
    </ToastProvider>
  );
}

function LedgerInner() {
  const { retailerId } = useParams<{ retailerId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(true);
  const [wallet, setWallet] = useState<AdminWalletRow | null>(null);
  const [wallets, setWallets] = useState<AdminWalletRow[]>([]);
  const [retailer, setRetailer] = useState<{ id: string; name: string; shopName: string } | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [rows, setRows] = useState<WalletTxn[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [mandateCount, setMandateCount] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deductOpen, setDeductOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, type, range]);

  const loadWallet = useCallback(async () => {
    if (!retailerId) return;
    try {
      const [summaryRes, retailerRes] = await Promise.allSettled([
        walletService.getAdminSummary(),
        adminService.getRetailerById(retailerId),
      ]);

      if (summaryRes.status === "fulfilled") {
        const all: AdminWalletRow[] = summaryRes.value?.data?.wallets ?? [];
        setWallets(all);
        setWallet(all.find((w) => w.retailerId === retailerId) ?? null);
      }
      if (retailerRes.status === "fulfilled" && retailerRes.value?.data?.retailer) {
        const r = retailerRes.value.data.retailer;
        setRetailer({ id: r.id, name: r.name, shopName: r.shopName });
      } else {
        setNotFound(true);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load this wallet");
    } finally {
      setLoading(false);
    }
  }, [retailerId, toast]);

  const loadLedger = useCallback(async () => {
    if (!retailerId) return;
    setTxnLoading(true);
    try {
      const { dateFrom, dateTo } = rangeToDates(range);
      const res = await walletService.getAdminTransactions({
        retailerId,
        search,
        type,
        dateFrom,
        dateTo,
        page,
        limit: pageSize,
      });
      setRows(res?.data?.transactions ?? []);
      setTotal(res?.data?.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load the ledger");
      setRows([]);
    } finally {
      setTxnLoading(false);
    }
  }, [retailerId, search, type, range, page, pageSize, toast]);

  const loadMandateCount = useCallback(async () => {
    if (!retailerId) return;
    try {
      const res = await walletService.getAdminTransactions({
        retailerId,
        category: "Mandate Creation",
        limit: 1,
      });
      setMandateCount(res?.data?.pagination?.total ?? 0);
    } catch {
      setMandateCount(0);
    }
  }, [retailerId]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  useEffect(() => {
    loadMandateCount();
  }, [loadMandateCount]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadWallet(), loadLedger(), loadMandateCount()]);
  }, [loadWallet, loadLedger, loadMandateCount]);

  async function addBalance(id: string, amount: number, reason: string, remark: string) {
    try {
      const res = await walletService.adjustBalance({ retailerId: id, type: "CREDIT", amount, reason: remark || reason });
      toast.success(res?.message || "Balance added to wallet");
      setAddOpen(false);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not add balance");
    }
  }

  async function deductBalance(id: string, amount: number, reason: string, remark: string) {
    try {
      const res = await walletService.adjustBalance({ retailerId: id, type: "DEBIT", amount, reason: remark || reason });
      toast.success(res?.message || "Balance deducted from wallet");
      setDeductOpen(false);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not deduct balance");
    }
  }

  function openMandate(refNo: string) {
    navigate(`/admin/mandates/${refNo}`);
  }

  function resetFilters() {
    setSearchInput("");
    setType("");
    setRange(DEFAULT_RANGE);
  }

  if (!loading && (notFound || !retailer)) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-6">
        <EmptyState title="Retailer not found" description="This retailer could not be found." actionLabel="Back to Wallets" onAction={() => navigate("/admin/wallets")} />
      </div>
    );
  }

  const available = wallet?.balance ?? 0;

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <button
        type="button"
        onClick={() => navigate("/admin/wallets")}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink cursor-pointer"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Wallets
      </button>

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-50 text-lg font-semibold text-primary-500">
            {(retailer?.shopName ?? "?").slice(0, 1)}
          </span>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{retailer?.shopName ?? "Wallet"}</h1>
            <p className="mt-1 text-[13px] text-ink-secondary">
              {retailer?.name ?? "—"} · {retailerId} · Wallet ledger
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <Button variant="secondary" onClick={() => setDeductOpen(true)} leftIcon={<Minus className="h-4 w-4" />}>
            Deduct Balance
          </Button>
          <Button onClick={() => setAddOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
            Add Balance
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <SkeletonBlock className="h-40 w-full rounded-[12px]" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
            <SkeletonBlock className="h-28 rounded-[10px]" />
          </div>
          <SkeletonBlock className="h-96 w-full rounded-[10px]" />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-6 rounded-[12px] bg-primary-500 p-6 md:grid-cols-2 md:gap-0 md:divide-x md:divide-white/20">
            <div className="md:pr-8">
              <p className="text-[13px] text-white/80">Available Balance</p>
              <p className="mt-1.5 text-[32px] font-bold leading-tight text-white">{formatINR(available)}</p>
              <p className="mt-1.5 text-xs text-white/70">
                Last recharged on {wallet?.lastRechargeAt ? formatDate(wallet.lastRechargeAt) : "—"}
              </p>
            </div>
            <div className="md:pl-8">
              <p className="text-[13px] text-white/80">Pending Top-ups</p>
              <p className="mt-1.5 text-2xl font-semibold leading-tight text-white">{wallet?.pendingTopups ?? 0}</p>
              <p className="mt-1.5 text-xs text-white/70">Requests awaiting your review</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SummaryCard label="Lifetime Credited" value={formatINR(wallet?.lifetimeCredited ?? 0)} sub="Total credits to wallet" />
            <SummaryCard label="Lifetime Spent" value={formatINR(wallet?.lifetimeSpent ?? 0)} sub="Total charges collected" />
            <SummaryCard
              label="Mandates Created"
              value={`${mandateCount}`}
              sub="Creation charges applied"
              onClick={() => navigate(`/admin/mandates?retailer=${retailerId}`)}
            />
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-ink">Transaction Ledger</h3>
            <TransactionsTab
              loading={txnLoading}
              search={searchInput}
              onSearch={setSearchInput}
              retailer=""
              onRetailer={() => undefined}
              type={type}
              onType={setType}
              range={range}
              onRange={setRange}
              rangeOpen={rangeOpen}
              onRangeOpen={setRangeOpen}
              onReset={resetFilters}
              rows={rows}
              retailerOptions={[]}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              openMandate={openMandate}
              showCreatedBy
              showExport={false}
            />
          </div>
        </div>
      )}

      <AddBalanceModal open={addOpen} onClose={() => setAddOpen(false)} wallets={wallets} targetRetailerId={retailerId ?? null} onAdd={addBalance} />
      <DeductBalanceModal open={deductOpen} onClose={() => setDeductOpen(false)} wallets={wallets} targetRetailerId={retailerId ?? null} onDeduct={deductBalance} />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  onClick,
}: {
  label: string;
  value: string;
  sub: string;
  onClick?: () => void;
}) {
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-[10px] border border-line bg-card p-5 shadow-card transition-all ${
        isClickable
          ? "cursor-pointer hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0"
          : ""
      }`}
    >
      <span className="text-[13px] text-ink-secondary">{label}</span>
      <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
      <div className="mt-2 text-xs text-ink-muted">{sub}</div>
    </div>
  );
}